import React, { useState, useEffect, useRef } from 'react';

/**
 * Animated Stats Counter Section
 * Numbers count up when scrolled into view
 */

function useCountUp(end, duration = 2000, start = 0) {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * (end - start) + start));

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [isVisible, end, start, duration]);

  return { count, ref };
}

function StatCard({ number, suffix = '', label, icon, delay = 0 }) {
  const { count, ref } = useCountUp(number, 2500);

  return (
    <div 
      ref={ref}
      className="relative group"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#e31937]/20 to-transparent rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      <div className="relative glass-card rounded-xl md:rounded-2xl p-5 md:p-8 group-hover:border-[#e31937]/30 transition-all duration-500">
        {/* Animated Icon Container */}
        <div className="relative mb-3 md:mb-5">
          <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-[#e31937]/20 to-[#e31937]/5 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
            <span className="text-2xl md:text-3xl">{icon}</span>
          </div>
        </div>
        
        {/* Animated Number */}
        <div className="text-3xl md:text-5xl font-black text-white mb-2 md:mb-3 tracking-tight">
          <span className="inline-block group-hover:scale-110 transition-transform duration-300 origin-left">
            {count.toLocaleString('hu-HU')}
          </span>
          <span className="gradient-text ml-1">{suffix}</span>
        </div>
        
        {/* Label with underline effect */}
        <p className="text-gray-400 text-sm md:text-base font-medium relative inline-block">
          {label}
          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#e31937] group-hover:w-full transition-all duration-300" />
        </p>
      </div>
    </div>
  );
}

export default function StatsSection() {
  const stats = [
    { number: 20, suffix: '+', label: 'Év tapasztalat', icon: '🏆' },
    { number: 600, suffix: '+', label: 'Elkészült otthon', icon: '🏠' },
    { number: 22, suffix: '', label: 'Telephely országszerte', icon: '📍' },
    { number: 98, suffix: '%', label: 'Elégedett ügyfél', icon: '⭐' },
  ];

  const badges = [
    { icon: '🇭🇺', label: 'Magyar gyártás' },
    { icon: '🛡️', label: '5 év garancia' },
    { icon: '⚡', label: '2-4 hét telepítés' },
    { icon: '🌱', label: 'Energiahatékony' },
  ];

  return (
    <section className="py-20 md:py-32 bg-gradient-to-b from-[#1a1a1a] via-[#0a0a0a] to-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#e31937]/10 rounded-full blur-[150px] animate-pulse-slow" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-900/10 rounded-full blur-[180px] animate-pulse-slow" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Particles */}
      <div className="absolute inset-0 particles-bg opacity-20" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 backdrop-blur-xl border border-blue-500/30 rounded-full px-6 py-3 mb-8 shadow-2xl shadow-blue-500/20">
            <span className="text-blue-400 font-bold text-sm tracking-wider">📊 EREDMÉNYEINK</span>
          </div>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 tracking-tight leading-tight">
            Számokban a <span className="bg-gradient-to-r from-secondary via-red-500 to-secondary bg-clip-text text-transparent">HoppHomes</span>
          </h2>
          <p className="text-gray-300 text-lg md:text-2xl max-w-3xl mx-auto leading-relaxed font-light">
            Több mint két évtizedes tapasztalattal és több száz elégedett ügyféllel a hátunk mögött építjük a jövő otthonait.
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <StatCard
              key={stat.label}
              number={stat.number}
              suffix={stat.suffix}
              label={stat.label}
              icon={stat.icon}
              delay={index * 100}
            />
          ))}
        </div>

        {/* Trust badges - enhanced design */}
        <div className="hidden md:flex mt-16 flex-wrap justify-center gap-4 items-center">
          {badges.map((badge, i) => (
            <div 
              key={badge.label}
              className="group flex items-center gap-3 px-5 py-3 bg-white/5 hover:bg-white/10 rounded-full border border-white/10 hover:border-[#e31937]/30 transition-all duration-300 cursor-pointer backdrop-blur-sm"
            >
              <span className="text-2xl group-hover:scale-110 transition-transform duration-300">{badge.icon}</span>
              <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-300">{badge.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
